import { IChatMessage, IChatSession, IChatPreference } from 'src/interface/IChatState';

export const actionTypes = {
  // Chat widget
  OPEN_CHAT: 'OPEN_CHAT',
  CLOSE_CHAT: 'CLOSE_CHAT',
  TOGGLE_CHAT: 'TOGGLE_CHAT',

  // Sessions
  FETCH_SESSIONS_REQUEST: 'FETCH_SESSIONS_REQUEST',
  FETCH_SESSIONS_SUCCESS: 'FETCH_SESSIONS_SUCCESS',
  FETCH_SESSIONS_ERROR: 'FETCH_SESSIONS_ERROR',

  CREATE_SESSION_REQUEST: 'CREATE_SESSION_REQUEST',
  CREATE_SESSION_SUCCESS: 'CREATE_SESSION_SUCCESS',
  CREATE_SESSION_ERROR: 'CREATE_SESSION_ERROR',

  SET_ACTIVE_SESSION: 'SET_ACTIVE_SESSION',

  // Messages
  FETCH_MESSAGES_REQUEST: 'FETCH_MESSAGES_REQUEST',
  FETCH_MESSAGES_SUCCESS: 'FETCH_MESSAGES_SUCCESS',
  FETCH_MESSAGES_ERROR: 'FETCH_MESSAGES_ERROR',

  SEND_MESSAGE_REQUEST: 'SEND_MESSAGE_REQUEST',
  SEND_MESSAGE_SUCCESS: 'SEND_MESSAGE_SUCCESS',
  SEND_MESSAGE_ERROR: 'SEND_MESSAGE_ERROR',

  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',

  // WebSocket
  CONNECT_WEBSOCKET_REQUEST: 'CONNECT_WEBSOCKET_REQUEST',
  CONNECT_WEBSOCKET_SUCCESS: 'CONNECT_WEBSOCKET_SUCCESS',
  CONNECT_WEBSOCKET_ERROR: 'CONNECT_WEBSOCKET_ERROR',
  DISCONNECT_WEBSOCKET: 'DISCONNECT_WEBSOCKET',
  WEBSOCKET_MESSAGE_RECEIVED: 'WEBSOCKET_MESSAGE_RECEIVED',

  SET_TYPING: 'SET_TYPING',

  // Preferences
  FETCH_PREFERENCES_REQUEST: 'FETCH_PREFERENCES_REQUEST',
  FETCH_PREFERENCES_SUCCESS: 'FETCH_PREFERENCES_SUCCESS',
  FETCH_PREFERENCES_ERROR: 'FETCH_PREFERENCES_ERROR',

  UPDATE_PREFERENCES_REQUEST: 'UPDATE_PREFERENCES_REQUEST',
  UPDATE_PREFERENCES_SUCCESS: 'UPDATE_PREFERENCES_SUCCESS',
  UPDATE_PREFERENCES_ERROR: 'UPDATE_PREFERENCES_ERROR',

  // Clear
  CLEAR_CHAT_ERROR: 'CLEAR_CHAT_ERROR',
};

// Chat widget actions
export const openChat = () => ({
  type: actionTypes.OPEN_CHAT,
});

export const closeChat = () => ({
  type: actionTypes.CLOSE_CHAT,
});

export const toggleChat = () => ({
  type: actionTypes.TOGGLE_CHAT,
});

// Session actions
export const fetchSessionsRequest = () => ({
  type: actionTypes.FETCH_SESSIONS_REQUEST,
});

export const fetchSessionsSuccess = (sessions: IChatSession[]) => ({
  type: actionTypes.FETCH_SESSIONS_SUCCESS,
  payload: { sessions },
});

export const fetchSessionsError = (error: string) => ({
  type: actionTypes.FETCH_SESSIONS_ERROR,
  payload: { error },
});

export const createSessionRequest = (language: string = 'vi') => ({
  type: actionTypes.CREATE_SESSION_REQUEST,
  payload: { language },
});

export const createSessionSuccess = (session: IChatSession) => ({
  type: actionTypes.CREATE_SESSION_SUCCESS,
  payload: { session },
});

export const createSessionError = (error: string) => ({
  type: actionTypes.CREATE_SESSION_ERROR,
  payload: { error },
});

export const setActiveSession = (sessionId: string) => ({
  type: actionTypes.SET_ACTIVE_SESSION,
  payload: { sessionId },
});

// Message actions
export const fetchMessagesRequest = (sessionId: string) => ({
  type: actionTypes.FETCH_MESSAGES_REQUEST,
  payload: { sessionId },
});

export const fetchMessagesSuccess = (sessionId: string, messages: IChatMessage[]) => ({
  type: actionTypes.FETCH_MESSAGES_SUCCESS,
  payload: { sessionId, messages },
});

export const fetchMessagesError = (error: string) => ({
  type: actionTypes.FETCH_MESSAGES_ERROR,
  payload: { error },
});

export const sendMessageRequest = (sessionId: string, content: string, language: string, pageContext?: any) => ({
  type: actionTypes.SEND_MESSAGE_REQUEST,
  payload: { sessionId, content, language, pageContext },
});

export const sendMessageSuccess = (message: IChatMessage) => ({
  type: actionTypes.SEND_MESSAGE_SUCCESS,
  payload: { message },
});

export const sendMessageError = (error: string) => ({
  type: actionTypes.SEND_MESSAGE_ERROR,
  payload: { error },
});

export const addMessage = (sessionId: string, message: IChatMessage) => ({
  type: actionTypes.ADD_MESSAGE,
  payload: { sessionId, message },
});

export const updateMessage = (sessionId: string, messageId: string, updates: Partial<IChatMessage>) => ({
  type: actionTypes.UPDATE_MESSAGE,
  payload: { sessionId, messageId, updates },
});

// WebSocket actions
export const connectWebSocketRequest = (sessionId: string) => ({
  type: actionTypes.CONNECT_WEBSOCKET_REQUEST,
  payload: { sessionId },
});

export const connectWebSocketSuccess = () => ({
  type: actionTypes.CONNECT_WEBSOCKET_SUCCESS,
});

export const connectWebSocketError = (error: string) => ({
  type: actionTypes.CONNECT_WEBSOCKET_ERROR,
  payload: { error },
});

export const disconnectWebSocket = () => ({
  type: actionTypes.DISCONNECT_WEBSOCKET,
});

export const websocketMessageReceived = (data: any) => ({
  type: actionTypes.WEBSOCKET_MESSAGE_RECEIVED,
  payload: data,
});

export const setTyping = (typing: boolean) => ({
  type: actionTypes.SET_TYPING,
  payload: { typing },
});

// Preferences actions
export const fetchPreferencesRequest = () => ({
  type: actionTypes.FETCH_PREFERENCES_REQUEST,
});

export const fetchPreferencesSuccess = (preferences: IChatPreference) => ({
  type: actionTypes.FETCH_PREFERENCES_SUCCESS,
  payload: { preferences },
});

export const fetchPreferencesError = (error: string) => ({
  type: actionTypes.FETCH_PREFERENCES_ERROR,
  payload: { error },
});

export const updatePreferencesRequest = (preferences: Partial<IChatPreference>) => ({
  type: actionTypes.UPDATE_PREFERENCES_REQUEST,
  payload: { preferences },
});

export const updatePreferencesSuccess = (preferences: IChatPreference) => ({
  type: actionTypes.UPDATE_PREFERENCES_SUCCESS,
  payload: { preferences },
});

export const updatePreferencesError = (error: string) => ({
  type: actionTypes.UPDATE_PREFERENCES_ERROR,
  payload: { error },
});

// Clear error
export const clearChatError = () => ({
  type: actionTypes.CLEAR_CHAT_ERROR,
});

// Action type union for type safety
export type ChatAction =
  | ReturnType<typeof openChat>
  | ReturnType<typeof closeChat>
  | ReturnType<typeof toggleChat>
  | ReturnType<typeof fetchSessionsRequest>
  | ReturnType<typeof fetchSessionsSuccess>
  | ReturnType<typeof fetchSessionsError>
  | ReturnType<typeof createSessionRequest>
  | ReturnType<typeof createSessionSuccess>
  | ReturnType<typeof createSessionError>
  | ReturnType<typeof setActiveSession>
  | ReturnType<typeof fetchMessagesRequest>
  | ReturnType<typeof fetchMessagesSuccess>
  | ReturnType<typeof fetchMessagesError>
  | ReturnType<typeof sendMessageRequest>
  | ReturnType<typeof sendMessageSuccess>
  | ReturnType<typeof sendMessageError>
  | ReturnType<typeof addMessage>
  | ReturnType<typeof updateMessage>
  | ReturnType<typeof connectWebSocketRequest>
  | ReturnType<typeof connectWebSocketSuccess>
  | ReturnType<typeof connectWebSocketError>
  | ReturnType<typeof disconnectWebSocket>
  | ReturnType<typeof websocketMessageReceived>
  | ReturnType<typeof setTyping>
  | ReturnType<typeof fetchPreferencesRequest>
  | ReturnType<typeof fetchPreferencesSuccess>
  | ReturnType<typeof fetchPreferencesError>
  | ReturnType<typeof updatePreferencesRequest>
  | ReturnType<typeof updatePreferencesSuccess>
  | ReturnType<typeof updatePreferencesError>
  | ReturnType<typeof clearChatError>;
